'use client';

/**
 * React Query Provider Component
 *
 * Wraps the application with QueryClientProvider to enable React Query functionality.
 * Must be a client component since QueryClientProvider requires client-side functionality.
 *
 * Features:
 * - Provides React Query cache to all child components
 * - Includes DevTools in development for debugging
 * - Persists user preferences to localStorage automatically
 * - Uses centralized queryClient configuration from lib/query-client.ts
 * - Automatically cleans up deprecated localStorage keys on first load
 *
 * Usage:
 * Already included in app/layout.tsx - no need to use directly
 */

import { useState, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { runCleanupIfNeeded } from '@/lib/platform/utils/storage-cleanup';

interface QueryProviderProps {
  children: React.ReactNode;
}

export default function QueryProvider({ children }: QueryProviderProps) {
  // Create a new QueryClient instance per request to avoid sharing cache between users
  // This is important for SSR/server components
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Performance Optimization: Cache data for 5 minutes (reduces API calls)
            staleTime: 5 * 60 * 1000,

            // Garbage collection time - unused data is removed after 10 minutes
            gcTime: 10 * 60 * 1000,

            // Retry failed requests up to 3 times
            retry: 3,

            // Exponential backoff for retries
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

            // Performance: Disable refetch on window focus to reduce unnecessary requests
            refetchOnWindowFocus: false,

            // Refetch when internet connection is restored
            refetchOnReconnect: true,

            // Don't refetch on mount if data is still fresh
            refetchOnMount: true,
          },
          mutations: {
            // Retry mutations once on failure
            retry: 1,

            // Exponential backoff
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      })
  );

  // Create persister for localStorage (only persist user preferences, not API data)
  const [persister] = useState(() => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return undefined;
    }

    return createSyncStoragePersister({
      storage: window.localStorage,
      // Only persist queries with meta.persist: true
      // This prevents API data from being persisted (we only want user preferences)
      key: 'REACT_QUERY_OFFLINE_CACHE',
    });
  });

  // Run storage cleanup on mount (removes deprecated localStorage keys)
  useEffect(() => {
    runCleanupIfNeeded();
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: persister!,
        // Only persist queries that explicitly opt-in via meta.persist
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return query.meta?.persist === true;
          },
        },
      }}
    >
      {children}
      {/* DevTools only loaded in development. bottom-left so it doesn't cover the AI orb */}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </PersistQueryClientProvider>
  );
}
